import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import * as React from 'react';
import { asCurrencyFormat, asPercentageValue } from '../../../utils/parse';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import ModalButtons from '../../ui/modals/ModalButtons';
import { APIStatus } from '../../../utils/types';
import { fetchGroupRates, setRerateInputs, submitRerateResponse } from '../../../store/group';
import { Message } from 'primereact/message';
import { CalculatorApiApiCalculatorGetRequest, GroupServiceGetResponse, RateReliefCalculatorServiceResponse } from '../../../services/api';
import debounce from 'debounce';
import { useAppDispatch } from '../../../store';
import { classNames } from 'primereact/utils';
import { flattenTimePeriodDate } from '../../../utils/date';
import MinMaxCompRates from './MinMaxCompRates';

export default function RerateResponseForm({ group, rates, inputRR, onComplete }: { group: GroupServiceGetResponse, rates: RateReliefCalculatorServiceResponse , inputRR: number | null, onComplete: () => void }) {
  const dispatch = useAppDispatch();
  const [showingConfirmationModal, setShowingConfirmationModal] = React.useState<boolean>(false);
  const [notes, setNotes] = React.useState<string>('');
  const [submissionState, setSubmissionState] = React.useState<APIStatus|null>(null);

  const  [currentPremiumEQ, setCurrentPremiumEQ] = React.useState<number>();
  const  [newPremiumEQ, setNewPremiumEQ] = React.useState<number>();
  const  [employeeMedicalChild, setEmployeeMedicalChild] = React.useState<number>();
  const  [employeeMedicalEmployee, setEmployeeMedicalEmployee] = React.useState<number>();
  const  [employeeMedicalFamily, setEmployeeMedicalFamily] = React.useState<number>();
  const  [employeeMedicalSpouse, setEmployeeMedicalSpouse] = React.useState<number>();
  const  [estimatedStandardNBRate, setEstimatedStandardNBRate] = React.useState<number>();
  const  [reissueType, setReissueType] = React.useState<string>();

  const premiumDiff = React.useMemo(() => {
      return (newPremiumEQ ?? 0) - (currentPremiumEQ ?? 0);
    }, [newPremiumEQ, currentPremiumEQ]);

  const total = React.useMemo<number>(() => {
    return employeeMedicalChild + employeeMedicalEmployee + employeeMedicalFamily + employeeMedicalSpouse;
  }, [employeeMedicalChild, employeeMedicalEmployee, employeeMedicalFamily, employeeMedicalSpouse]);

  const increase = React.useMemo<number>(() => {
    if (!newPremiumEQ && currentPremiumEQ) return 0;
    return (newPremiumEQ - currentPremiumEQ) / currentPremiumEQ;
  }, [currentPremiumEQ, newPremiumEQ]);
  
  const doCalculations = React.useCallback(
    debounce(({
      currentPremiumEQ,
      employeeMedicalChild,
      employeeMedicalEmployee,
      employeeMedicalFamily,
      estimatedStandardNBRate,
      employeeMedicalSpouse,
      newPremiumEQ,
      reissueType,
      optionsShutoff,
      override,
      rateReliefAmount
    }: CalculatorApiApiCalculatorGetRequest) => {

      const inputs = {
        policyNumber: group.policyDetailModel?.policyNumber!,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
        employeeMedicalChild,
        employeeMedicalEmployee,
        employeeMedicalFamily,
        employeeMedicalSpouse,
        rateReliefAmount,
        ...currentPremiumEQ && { currentPremiumEQ },
        ...estimatedStandardNBRate && { estimatedStandardNBRate },
        ...newPremiumEQ && { newPremiumEQ },
        ...reissueType && { reissueType },
        ...optionsShutoff && { optionsShutoff },
        ...override && { override },
      }

      dispatch(setRerateInputs(inputs));
      dispatch(fetchGroupRates(inputs));
    }, 500), []
  );
  
  function setGroupInitialRerateValues() {
    setCurrentPremiumEQ(group.policyDetailModel?.currentPremiumEquivalent?.toString() || '');
    setNewPremiumEQ(group.policyDetailModel?.newPremiumEquivalent?.toString() || '');
    setEmployeeMedicalChild(group.policyDetailModel?.employeeMedCountEC || 0);
    setEmployeeMedicalEmployee(group.policyDetailModel?.employeeMedCountEE || 0);
    setEmployeeMedicalFamily(group.policyDetailModel?.employeeMedCountFF || 0);
    setEmployeeMedicalSpouse(group.policyDetailModel?.employeeMedCountES || 0);
    setEstimatedStandardNBRate(parseInt(group.policyDetailModel?.estimatedStandardNBRate || 0), 10);
    setReissueType(group.policyDetailModel?.reissueType || '');
  }

  React.useEffect(setGroupInitialRerateValues, []);
  React.useEffect(() => {
    doCalculations({
      currentPremiumEQ,
      employeeMedicalChild,
      employeeMedicalEmployee,
      employeeMedicalFamily,
      employeeMedicalSpouse,
      estimatedStandardNBRate,
      newPremiumEQ,
      reissueType,
      rateReliefAmount: inputRR / 100,
      override: group.policyDetailModel?.override,
      optionsShutoff: group.policyDetailModel?.optionsShutoff,
    });    
  }, [
    currentPremiumEQ,
    employeeMedicalChild,
    employeeMedicalEmployee,
    employeeMedicalFamily,
    employeeMedicalSpouse,
    estimatedStandardNBRate,
    newPremiumEQ,
    reissueType,
    inputRR,
  ]);

  function hideConfirmationForm() {
    setShowingConfirmationModal(false);
    setNotes('');
    setSubmissionState(null);
  }

  async function submitForm() {
    try {
      setSubmissionState(APIStatus.LOADING);
      await submitRerateResponse({
        note: notes,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
        policyNumber: group.policyDetailModel?.policyNumber!,
        employeeMedicalChild,
        employeeMedicalEmployee,
        employeeMedicalFamily,
        employeeMedicalSpouse,
        ...currentPremiumEQ && { currentPremiumEQ },
        ...estimatedStandardNBRate && { estimatedStandardNBRate },
        ...newPremiumEQ && { newPremiumEQ },
        ...reissueType && { reissueType },
      });
      
      onComplete();
      setSubmissionState(APIStatus.COMPLETE);
    } catch {
      setSubmissionState(APIStatus.ERROR);
    }
  }

  function handlePasteOnInputNumber(e: React.ClipboardEvent<HTMLSpanElement>, callbackFn: (value: number) => void) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    const numericValue = parseFloat(pastedData.replace(/[^0-9.-]+/g, ''));

    callbackFn(numericValue);
  }
  
  return (
    <>
      <div className="flex flex-col gap-4" data-rerate-response-form>
        <p>Evaluate this group by completing the form below. This will enable you to view the impact and establish
        suitable revised rate relief levels for Account Management:</p>

        <div className="flex flex-col gap-4 p-6 border rounded border-allstate-light-blue">
          <h3 className="text-lg font-medium">Employee medical count</h3>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="ee_count">Employee</label>
              <InputNumber value={employeeMedicalEmployee} onChange={(e) => setEmployeeMedicalEmployee(e.value)} inputId="ee_count" min={0} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="spouse_count">Spouse</label>
              <InputNumber value={employeeMedicalSpouse} onChange={(e) => setEmployeeMedicalSpouse(e.value)} inputId="spouse_count" min={0} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="ch_count">Child</label>
              <InputNumber value={employeeMedicalChild} onChange={(e) => setEmployeeMedicalChild(e.value)} inputId="ch_count" min={0} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="ff_count">Family</label>
              <InputNumber value={employeeMedicalFamily} onChange={(e) => setEmployeeMedicalFamily(e.value)} inputId="ff_count" min={0} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="total">Total</label>
              <InputNumber disabled value={total} inputId="total" min={0} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="old_total">Old total</label>
              <InputNumber disabled value={group.policyDetailModel?.employeeMedCountOldTOT} inputId="old_total" min={0} />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 p-6 border rounded border-accent-green">
          <h3 className="text-lg font-medium">Premium information</h3>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="current_premium_eq">Current premium EQ</label>
              <InputNumber value={currentPremiumEQ} onPaste={(e) => handlePasteOnInputNumber(e, setCurrentPremiumEQ)} onChange={(e) => setCurrentPremiumEQ(e.value!)} inputId="current_premium_eq" min={0} prefix="$" maxFractionDigits={2} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="new_premium_eq">New premium EQ</label>
              <InputNumber value={newPremiumEQ} onPaste={(e) => handlePasteOnInputNumber(e, setNewPremiumEQ)} onChange={(e) => setNewPremiumEQ(e.value!)} inputId="new_premium_eq" min={0} prefix="$" maxFractionDigits={2} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="increase">Increase</label>
              <InputNumber disabled value={(increase || 0) * 100} inputId="increase" suffix="%" maxFractionDigits={2} />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 p-6 border rounded border-accent-purple">
          <h3 className="text-lg font-medium">Rate information</h3>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="est_std_rate">Estimated standard NB rate</label>
              <InputNumber value={estimatedStandardNBRate} onPaste={(e) => handlePasteOnInputNumber(e, setEstimatedStandardNBRate)} onChange={(e) => setEstimatedStandardNBRate(e.value)} inputId="est_std_rate" min={0} prefix="$" maxFractionDigits={0} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold" htmlFor="reissue_type">Reissue type</label>
              <Dropdown options={[{ label: 'Flag 1', value: 'Flag 1' }, { label: 'Flag 2+', value: 'Flag 2+'}]} value={reissueType} onChange={(e) => setReissueType(e.value)} inputId="reissue_type" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 p-6 border rounded border-warning-yellow">
          <h3 className="text-lg font-medium">Impact summary</h3>

          <div className="flex flex-wrap justify-between gap-4">
            <div className="flex-1 font-bold">Dollar / percentage</div>
            {premiumDiff !== 0 && (
              <Badge
                severity={premiumDiff > 0 ? "success" : "warning"}
                value={
                  <span className="flex items-center gap-1">
                    <span className={
                      classNames(
                        "text-2xs pi",
                        {
                          "pi-arrow-up": premiumDiff > 0,
                          "pi-arrow-down": premiumDiff < 0,
                        }
                    )}></span>
                    <span>{asCurrencyFormat(Math.abs(premiumDiff))}</span>
                    <span>
                      {asPercentageValue(increase * 100)} {premiumDiff > 0 ? 'increase' : 'decrease'}
                    </span>
                  </span>
                }
              />
            )}
          </div>
        </div>

        <MinMaxCompRates rates={rates} />

        <div className="grid grid-cols-2 gap-4">
          <Button rounded severity="secondary" label="reset form" onClick={() => setGroupInitialRerateValues()} />
          <Button rounded label="save changes" onClick={() => setShowingConfirmationModal(true)} />
        </div>
      </div>

      <Dialog
        visible={showingConfirmationModal}
        draggable={false}
        header={
          <>
            <div>Confirm changes</div>
            <div className="text-base font-normal">Justification for Account Manager</div>
          </>
        }
        onHide={hideConfirmationForm}
        footer={
          <ModalButtons
            isLoading={submissionState === APIStatus.LOADING}
            isFormValid={!!notes}
            onCancel={hideConfirmationForm}
            onSave={submitForm}
            saveLabel="confirm and notify AM" />
          }
      >
        <div className="flex flex-col gap-2">
          <div className="font-bold">Key factors that influenced this decision</div>
          <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5}></InputTextarea>
        </div>

        {submissionState === APIStatus.ERROR && (
          <div className="flex flex-col mt-4">
            <Message severity="error" text="There was an error submitting your request, please try again" />
          </div>
        )}
      </Dialog>
    </>
  )
}